;(function () {
  'use strict'

  angular
    .module('contrast.org-stats')
    .factory('OrgStatsService', OrgStatsService)

  function OrgStatsService(
    $q,
    $uibModal,
    $translate,
    d3ColorsService,
    d3,
    permissions,
    LicenseService,
    ServiceUtils,
    OrgStatsFactory,
    LicenseFactory,
    APPLICATION_GRADES,
    SERVER_ENVIRONMENT,
    D3_CONSTANTS,
    CS_DONUT_CONSTANTS,
    ORG_STATS_CONSTANTS,
    SERVER_ENVIRONMENT_ENUM,
    HIGHCHARTS_COLORS,
    CHART_CONFIG_ID
  ) {
    'ngInject'

    var PROTECT_USAGE_DROPDOWN_DATE_FORMAT = 'MMMM YYYY'
    var PROTECT_USAGE_PLAIN_DATE_FORMAT = 'YYYY MMM DD'

    var PROTECT_COLOR = '#3cc3b2'
    var ASSESS_COLOR = '#0da1a9'

    return {
      getLicenseStats: getLicenseStats,
      getLicenseBreakdowns: getLicenseBreakdowns,
      getAppStats: getAppStats,
      getAppStatsBreakdown: getAppStatsBreakdown,
      getAppStatsDrilldown: getAppStatsDrilldown,
      getProtectUsageHistory: getProtectUsageHistory,
      getProtectUsageHistoryOldestDate: getProtectUsageHistoryOldestDate,
      buildAppDonutBreakdown: buildAppDonutBreakdown,
      getServerStatsBreakdown: getServerStatsBreakdown,
      getServerStats: getServerStats,
      openShowMoreModal: openShowMoreModal,
      buildProtectUsageTrend: buildProtectUsageTrend,
      buildProtectUsageMetrics: buildProtectUsageMetrics,
      buildProtectUsageDropdownItems: buildProtectUsageDropdownItems
    }

    function getLicenseStats() {
      return $q
        .all({
          stats: _getLicenseStats(),
          trendData: _getLicenseHistory()
        })
        .then(function (response) {
          angular.extend(response.stats, {
            trendConfig: _buildLicenseTrend(
              response.trendData,
              response.stats.data.totalAssessment,
              response.stats.data.totalProtection
            )
          })

          return response.stats
        })
    }

    function getLicenseBreakdowns() {
      return $q.all({
        assessBreakdown: _getAssessmentLicenseBreakdown(),
        protectBreakdown: _getProtectionLicenseBreakdown()
      })
    }

    function getAppStats(expand) {
      return OrgStatsFactory.getAppStats(expand)
        .then(function (response) {
          var data = response.data

          return {
            data: {
              active: data.current
            },
            donutConfig: _getAppDonutConfig(data)
          }
        })
        .catch(ServiceUtils.handleError)
    }

    function getAppStatsBreakdown(expand, quickFilter) {
      return OrgStatsFactory.getAppStatsBreakdown(expand, quickFilter)
        .then(function (response) {
          var data = response.data

          return {
            data: {
              active: data.active,
              notSeen: data.unseen_apps,
              recentlyAdded: data.recently_onboarded_apps,
              critical: data.critical,
              unprotected: data.unprotected,
              protectedPercent:
                data.total_production > 0
                  ? Math.floor((100 * data.unprotected) / data.total_production)
                  : 0,
              totalProduction: data.total_production,
              expiring: data.expiring_soon
            },
            thermometerConfig: _getAppThermometerConfig(data)
          }
        })
        .catch(ServiceUtils.handleError)
    }

    function getAppStatsDrilldown(quickFilter) {
      return OrgStatsFactory.getAppStatsDrilldown(quickFilter)
        .then(function (response) {
          return response.data.applications
        })
        .catch(ServiceUtils.handleError)
    }

    function getProtectUsageHistoryOldestDate() {
      //find the oldest Protect License Usage data (1 year old as max)
      var todayFormat = moment().format(PROTECT_USAGE_PLAIN_DATE_FORMAT)
      var startDate = moment(todayFormat, PROTECT_USAGE_PLAIN_DATE_FORMAT)
        .utc()
        .startOf('day')
        .subtract(1, 'year')
      return LicenseFactory.getProtectUsageHistoryOldestDate(
        startDate.valueOf()
      )
        .then(function (response) {
          return response.data
        })
        .catch(ServiceUtils.handleError)
    }

    function getProtectUsageHistory(startDate, endDate) {
      return LicenseFactory.getProtectUsageHistory(startDate, endDate)
        .then(function (response) {
          return response.data
        })
        .catch(ServiceUtils.handleError)
    }

    function buildAppDonutBreakdown(data, breakdowns, selection) {
      var transformedData = []
      var selected
      var whereCondition
      if (angular.isDefined(selection)) {
        whereCondition = _getAppWhereCondition(selection)
      }

      _.each(breakdowns, function (breakdown) {
        switch (breakdown) {
          case ORG_STATS_CONSTANTS.APP_BREAKDOWNS.LANGUAGES:
            if (
              angular.isDefined(whereCondition) &&
              selection.group !== breakdown
            ) {
              transformedData.languages = _transformAppBreakdown(
                data,
                'language',
                whereCondition
              )
            } else {
              transformedData.languages = _transformAppBreakdown(
                data,
                'language'
              )
            }

            if (angular.isDefined(selection) && selection.group === breakdown) {
              selected = _.find(transformedData.languages, {
                name: selection.name
              })
              selected.selected = true
            }
            break

          case ORG_STATS_CONSTANTS.APP_BREAKDOWNS.SCORES:
            if (
              angular.isDefined(whereCondition) &&
              selection.group !== breakdown
            ) {
              transformedData.scores = _transformAppBreakdown(
                data,
                'grade',
                whereCondition
              )
            } else {
              transformedData.scores = _transformAppBreakdown(data, 'grade')
            }

            if (angular.isDefined(selection) && selection.group === breakdown) {
              selected = _.find(transformedData.scores, {
                name: selection.name
              })
              selected.selected = true
            }
            break

          case ORG_STATS_CONSTANTS.APP_BREAKDOWNS.TECHNOLOGIES:
            if (
              angular.isDefined(whereCondition) &&
              selection.group !== breakdown
            ) {
              transformedData.technologies = _transformAppBreakdown(
                data,
                'technologies',
                whereCondition
              )
            } else {
              transformedData.technologies = _transformAppBreakdown(
                data,
                'technologies'
              )
            }

            if (angular.isDefined(selection) && selection.group === breakdown) {
              selected = _.find(transformedData.technologies, {
                name: selection.name
              })
              selected.selected = true
            }
            break

          default:
            break
        }
      })

      return _getAppDonutConfig(transformedData)
    }

    function getServerStatsBreakdown(
      expand,
      quickFilter,
      environment,
      donutOptions
    ) {
      if (angular.isUndefined(SERVER_ENVIRONMENT[environment])) {
        environment = undefined
      }
      return OrgStatsFactory.getServerStatsBreakdown(
        expand,
        quickFilter,
        environment
      )
        .then(function (response) {
          var data = response.data

          return {
            breakdown: {
              deployed: data.deployed,
              online: data.online,
              assess: data.assess,
              protect: data.defend,
              assessPercent:
                data.deployed > 0
                  ? Math.floor((100 * data.assess) / data.deployed)
                  : 0,
              protectPercent:
                data.deployed > 0
                  ? Math.floor((100 * data.defend) / data.deployed)
                  : 0
            },
            donutConfig: _getServerDonutConfig(data, donutOptions)
          }
        })
        .catch(ServiceUtils.handleError)
    }

    function getServerStats(expand, quickFilter, limit) {
      return OrgStatsFactory.getServerStats(expand, quickFilter, limit)
        .then(function (response) {
          var data = response.data
          var environments = []

          if (data.dev > 0) {
            environments.push({
              name: SERVER_ENVIRONMENT.DEVELOPMENT.value,
              value: data.dev
            })
          }
          if (data.qa > 0) {
            environments.push({
              name: SERVER_ENVIRONMENT.QA.value,
              value: data.qa
            })
          }
          if (data.prod > 0) {
            environments.push({
              name: SERVER_ENVIRONMENT.PRODUCTION.value,
              value: data.prod
            })
          }

          return {
            thermometerConfig: _getServerThermometerConfig(environments),
            deployed: data.current,
            deleted: data.recently_deleted,
            onboarded: data.recently_onboarded,
            offline: data.offline_servers,
            deletedCount: data.deleted,
            onboardedCount: data.onboarded,
            offlineCount: data.offline
          }
        })
        .catch(ServiceUtils.handleError)
    }

    function openShowMoreModal(items, title, goToOverviewFn) {
      return $uibModal.open({
        templateUrl: 'views/servers/modal_more_entities.html',
        size: 'md',
        controller: function ($scope, $uibModalInstance) {
          angular.extend($scope, {
            items: items,
            title: title,
            cancel: cancel,
            goToOverview: goToOverview
          })

          function cancel() {
            $uibModalInstance.dismiss()
          }

          function goToOverview(item) {
            goToOverviewFn(item)
            $uibModalInstance.dismiss()
          }
        }
      })
    }

    function _getServerThermometerConfig(environments) {
      var transl = {
        title_serverEnvironmentDevelopment: $translate.instant(
          'title_serverEnvironmentDevelopment'
        ),
        title_serverEnvironmentQualityAssurance: $translate.instant(
          'title_serverEnvironmentQualityAssurance'
        ),
        title_serverEnvironmentProduction: $translate.instant(
          'title_serverEnvironmentProduction'
        )
      }

      return {
        count: _.reduce(
          environments,
          function (acc, env) {
            return env.value + acc
          },
          0
        ),
        data: _transformNVPs(
          environments,
          {
            count: 'value',
            class: function (env) {
              return 'therm-' + env.name.toLowerCase()
            },
            name: function (env) {
              if (env.name === SERVER_ENVIRONMENT.DEVELOPMENT.value) {
                return transl.title_serverEnvironmentDevelopment
              } else if (env.name === SERVER_ENVIRONMENT.QA.value) {
                return transl.title_serverEnvironmentQualityAssurance
              } else if (env.name === SERVER_ENVIRONMENT.PRODUCTION.value) {
                return transl.title_serverEnvironmentProduction
              }
            }
          },
          function (env) {
            return _.indexOf(SERVER_ENVIRONMENT_ENUM, env.name)
          }
        )
      }
    }

    function _getLicenseStats() {
      return OrgStatsFactory.getLicenseStats()
        .then(function (response) {
          var data = response.data
          var licenseData = {
            total: data.total_assessment + data.total_protection,
            totalAssessment: data.total_assessment,
            totalProtection: data.total_protection,
            assessUtilization:
              data.total_assessment !== 0
                ? Math.floor((100 * data.assessment) / data.total_assessment)
                : 0,
            protectUtilization:
              data.total_protection !== 0
                ? Math.floor((100 * data.protection) / data.total_protection)
                : 0,
            used: data.protection + data.assessment,
            protection: data.protection,
            assessment: data.assessment,
            unlicensedApps: data.unlicensed_apps,
            unlicensedServers: data.unlicensed_servers
          }

          return {
            data: licenseData,
            donutConfig: {
              overall: _getLicenseOverallDonutConfig(licenseData),
              assess: _getLicenseUtilizationDonutConfig(
                licenseData.assessUtilization
              ),
              protect: _getLicenseUtilizationDonutConfig(
                licenseData.protectUtilization
              )
            }
          }
        })
        .catch(ServiceUtils.handleError)
    }

    function _getLicenseHistory() {
      return OrgStatsFactory.getLicenseHistory()
        .then(function (response) {
          return response.data.license_history
        })
        .catch(ServiceUtils.handleError)
    }

    function _buildLicenseTrend(data, totalAssessment, totalProtection) {
      data = _.filter(data, function (d) {
        return angular.isNumber(d.assess) && angular.isNumber(d.protect)
      })

      if (data.length < 2) {
        data = []
      }

      var maxTotalLicensesValue = Math.max(totalAssessment, totalProtection)

      var showViewProtectUsageLink = false

      var startDate = _.reduce(
        data,
        function (currentMin, d) {
          return Math.min(d.timestamp, currentMin)
        },
        moment().valueOf()
      )
      var endDate = _.reduce(
        data,
        function (currentMin, d) {
          return Math.max(d.timestamp, currentMin)
        },
        moment().subtract(1, 'years').valueOf()
      )

      var format = D3_CONSTANTS.TIME_FORMATS.MONTH_ABBRV_AND_DAY

      if (moment(endDate).diff(moment(startDate), 'years') > 0) {
        format = D3_CONSTANTS.TIME_FORMATS.MONTH_ABBRV
      }

      var assessConfig = {
        tooltip: {
          message: $translate.instant('title_assessLicenses')
        },
        color: ASSESS_COLOR,
        id: CHART_CONFIG_ID.ASSESS_SERIES_CONFIG,
        values: _.map(data, function (d) {
          return d.assess
        })
      }
      var protectConfig = {
        tooltip: {
          message: $translate.instant('title_protectLicenses')
        },
        color: PROTECT_COLOR,
        id: CHART_CONFIG_ID.PROTECT_SERIES_CONFIG,
        values: _.map(data, function (d) {
          return d.protect
        }),

        drilldown: {
          tooltip: {
            message: $translate.instant(
              'action_licensesReports_viewProtectUsage'
            )
          },
          plotLine: {
            value: totalProtection
          }
        }
      }

      var assessPlotLineConfig = {
        color: HIGHCHARTS_COLORS.PLOT_LINE,
        value: totalAssessment,
        tooltip: {
          message: $translate.instant(
            'title_licensesReports_totalAssessLicenses'
          )
        }
      }

      var protectPlotLineConfig = {
        color: HIGHCHARTS_COLORS.PLOT_LINE,
        value: totalProtection,
        tooltip: {
          message: $translate.instant(
            'title_licensesReports_totalProtectLicenses'
          )
        }
      }

      var seriesConfig = [assessConfig]
      var plotLinesConfig = [assessPlotLineConfig]

      if (permissions.isRASPEnabled()) {
        seriesConfig.push(protectConfig)
        plotLinesConfig.push(protectPlotLineConfig)
        var maxProtectLicenses = _.maxBy(data, 'protect')
        var maxProtectLicensesValue = maxProtectLicenses
          ? maxProtectLicenses.protect
          : 0
        var zeroLicenses = _.filter(data, function (item) {
          return item.protect == 0
        })

        showViewProtectUsageLink =
          maxTotalLicensesValue > maxProtectLicensesValue * 10 ||
          zeroLicenses.length >= data.length / 2
      }

      return {
        xAxis: {
          formatFunction: function (val) {
            return d3.time.format(format)(val).toUpperCase()
          },
          values: _.map(data, function (d) {
            return d.timestamp
          }),
          startValue: startDate,
          endValue: endDate
        },
        yAxis: {
          seriesConfig: seriesConfig,
          plotLinesConfig: plotLinesConfig
        },
        data: {
          rawData: data,
          maxTotalLicensesValue: maxTotalLicensesValue,
          showViewProtectUsageLink: showViewProtectUsageLink
        }
      }
    }

    function buildProtectUsageTrend(data, totalProtectLicenses) {
      data = _.filter(data, function (d) {
        return angular.isNumber(d.totalServers) && angular.isNumber(d.usage)
      })

      if (data.length < 2) {
        data = []
      }

      var usageHoursData = _.map(data, function (d) {
        return d.usage
      })
      var agentUsageData = _.map(data, function (d) {
        return d.totalServers
      })

      var maxUsageHours = _.max(usageHoursData)
      var maxAgentUsage = _.max(agentUsageData)

      var startDate = _.reduce(
        data,
        function (currentMin, d) {
          return Math.min(d.timestamp, currentMin)
        },
        moment().valueOf()
      )
      var endDate = _.reduce(
        data,
        function (currentMin, d) {
          return Math.max(d.timestamp, currentMin)
        },
        moment().subtract(1, 'years').valueOf()
      )

      var dateFormat = D3_CONSTANTS.TIME_FORMATS.MONTH_NUM_AND_DAY

      if (moment(endDate).diff(moment(startDate), 'years') > 0) {
        dateFormat = D3_CONSTANTS.TIME_FORMATS.MONTH_ABBRV
      }

      var protectUsageHoursConfig = {
        tooltip: {
          message: $translate.instant(
            'title_licensesStatistics_usageHoursTooltip'
          )
        },
        color: PROTECT_COLOR,
        id: CHART_CONFIG_ID.PROTECT_USAGE_HOURS_SERIES_CONFIG,
        values: usageHoursData,
        maxValue: maxUsageHours,

        hasDrilldown: true,
        drilldown: {
          tooltip: {
            message: $translate.instant(
              'title_licensesStatistics_protectUsageHours'
            )
          }
        }
      }
      var protectAgentUsageConfig = {
        tooltip: {
          message: $translate.instant(
            'title_licensesStatistics_agentUsageTooltip'
          )
        },
        color: PROTECT_COLOR,
        id: CHART_CONFIG_ID.PROTECT_AGENT_USAGE_SERIES_CONFIG,
        values: agentUsageData,

        hasDrilldown: true,
        drilldown: {
          tooltip: {
            message: $translate.instant(
              'title_licensesStatistics_protectAgentsUsage'
            )
          },
          id: 'drilldown-protect-agent-usage-series-config'
        }
      }

      var protectPlotLineConfig = {
        color: HIGHCHARTS_COLORS.PLOT_LINE,
        value: totalProtectLicenses,
        tooltip: {
          message: $translate.instant(
            'title_licensesReports_totalProtectLicenses'
          )
        }
      }

      var seriesConfig = []
      var plotLinesConfig = []

      if (permissions.isRASPEnabled()) {
        seriesConfig.push(protectUsageHoursConfig)
        seriesConfig.push(protectAgentUsageConfig)
        plotLinesConfig.push(protectPlotLineConfig)
      }

      return {
        xAxis: {
          format: dateFormat,
          values: _.map(data, function (d) {
            return d.timestamp
          }),
          startValue: startDate,
          endValue: endDate
        },
        yAxis: {
          seriesConfig: seriesConfig,
          plotLinesConfig: plotLinesConfig
        },
        data: {
          rawData: data,
          maxUsageHours: maxUsageHours,
          maxAgentUsage: maxAgentUsage,
          totalProtectLicenses: totalProtectLicenses
        }
      }
    }

    function buildProtectUsageMetrics(data) {
      return {
        agentUsage: {
          avg: Math.round(data.agent_usage_metrics.avg_daily_protect_servers),
          peak: data.agent_usage_metrics.peak_daily_protect_servers,
          total: data.agent_usage_metrics.total_protect_servers
        },
        usageHours: {
          avg: Math.round(data.usage_hours_metrics.avg_daily_usage_hours),
          peak: data.usage_hours_metrics.peak_daily_usage_hours,
          total: data.usage_hours_metrics.total_usage_hours
        }
      }
    }

    function buildProtectUsageDropdownItems(oldestDate) {
      var items = []

      var todayFormat = moment().format(PROTECT_USAGE_PLAIN_DATE_FORMAT)
      var startOfToday = moment(todayFormat, PROTECT_USAGE_PLAIN_DATE_FORMAT)
        .utc()
        .startOf('day')
      var startOfCurrentMonth = moment(todayFormat).utc().startOf('month')
      var startOfOldestMonth = moment(oldestDate).startOf('month')

      items.push(
        _buildProtectUsageCurrentMonthItem(
          startOfCurrentMonth.clone(),
          startOfToday.clone()
        )
      )
      startOfCurrentMonth.subtract(1, 'months')

      while (
        startOfCurrentMonth.isAfter(startOfOldestMonth, 'month') ||
        startOfCurrentMonth.isSame(startOfOldestMonth, 'month')
      ) {
        items.push(_buildProtectUsageMonthItem(startOfCurrentMonth.clone()))
        startOfCurrentMonth.subtract(1, 'months')
      }

      return items
    }

    function _buildProtectUsageCurrentMonthItem(startDayOfMonth, today) {
      var value, startDate, endDate

      value = startDayOfMonth.format(PROTECT_USAGE_DROPDOWN_DATE_FORMAT)
      startDate = startDayOfMonth.valueOf()
      endDate = today.valueOf()

      return {
        value: value,
        startDate: startDate,
        endDate: endDate
      }
    }

    function _buildProtectUsageMonthItem(startDayOfMonth) {
      var value, startDate, endDate

      value = startDayOfMonth.format(PROTECT_USAGE_DROPDOWN_DATE_FORMAT)
      startDate = startDayOfMonth.valueOf()
      endDate = startDayOfMonth.clone().endOf('month').startOf('day').valueOf()

      return {
        value: value,
        startDate: startDate,
        endDate: endDate
      }
    }

    function _getAssessmentLicenseBreakdown() {
      return LicenseFactory.getAssessmentBreakdown()
        .then(function (response) {
          return {
            summary: _buildLicenseSummary(response.data.breakdown),
            timeline: _buildLicenseTimelineConfig(response.data.breakdown)
          }
        })
        .catch(ServiceUtils.handleError)
    }

    function _getProtectionLicenseBreakdown() {
      if (permissions.isRASPEnabled()) {
        return LicenseService.getProtectionBreakdown().then(function (
          breakdown
        ) {
          return {
            summary: _buildLicenseSummary(breakdown),
            timeline: _buildLicenseTimelineConfig(breakdown)
          }
        })
      } else {
        return $q.when()
      }
    }

    function _getLicenseOverallDonutConfig(data) {
      var transl = {
        title_licenseTypeAssess: $translate.instant('title_licenseTypeAssess'),
        title_licenseTypeProtect: $translate.instant(
          'title_licenseTypeProtect'
        ),
        title_unused_licenses: $translate.instant('title_unused_licenses')
      }

      var donutData = [
        {
          index: 0,
          value: data.assessment,
          name: transl.title_licenseTypeAssess,
          className: 'fill-link',
          id: 'assessment',
          width: CS_DONUT_CONSTANTS.DEFAULT_ARC_WIDTH * (6 / 5)
        },
        {
          index: 1,
          value: data.protection,
          name: transl.title_licenseTypeProtect,
          className: 'fill-teal',
          id: 'protection',
          width: CS_DONUT_CONSTANTS.DEFAULT_ARC_WIDTH * (6 / 5)
        }
      ]

      if (data.used < data.total) {
        donutData.push({
          index: 2,
          value: data.total - data.used,
          name: transl.title_unused_licenses,
          className: 'fill-gray-light',
          id: 'unused',
          width: CS_DONUT_CONSTANTS.DEFAULT_ARC_WIDTH
        })
      }

      return [
        {
          data: donutData,
          valueAccessor: 'value',
          classAccessor: 'className',
          idAccessor: 'id',
          sort: 'index',
          width: 'width',
          tooltipTemplate: "'views/tooltips/cs-donut-tooltip.html'"
        }
      ]
    }

    function _getLicenseUtilizationDonutConfig(utilization) {
      var data = []

      if (utilization > 0) {
        data.push({
          index: 0,
          id: 0,
          value: utilization,
          className: 'fill-green',
          width: CS_DONUT_CONSTANTS.DEFAULT_ARC_WIDTH * (3 / 5)
        })

        if (utilization !== 100) {
          data.push({
            index: 1,
            id: 1,
            value: 100 - utilization,
            className: 'fill-gray-light',
            width: CS_DONUT_CONSTANTS.DEFAULT_ARC_WIDTH / 2
          })
        }
      } else {
        data.push({
          index: 0,
          id: 0,
          value: 1,
          className: 'fill-gray-lighter',
          width: CS_DONUT_CONSTANTS.DEFAULT_ARC_WIDTH * (3 / 5)
        })
      }

      return [
        {
          data: data,
          valueAccessor: 'value',
          classAccessor: 'className',
          idAccessor: 'id',
          sort: 'index',
          width: 'width'
        }
      ]
    }

    function _buildLicenseSummary(breakdown) {
      var transl = {
        title_usedLicenses: $translate.instant('title_usedLicenses'),
        title_unusedLicenses: $translate.instant('title_unusedLicenses')
      }

      var summary = {
        count: breakdown.total,
        data: []
      }
      var unused =
        angular.isNumber(breakdown.revoke_count) &&
        breakdown.revoke_count > 0 &&
        breakdown.revoke_count < breakdown.unused
          ? breakdown.unused - breakdown.revoke_count
          : breakdown.unused
      var mapFn = function (item) {
        return {
          count: item.licenses,
          date: moment(item.expiration).format('MMM D, YYYY')
        }
      }

      if (breakdown.used > 0) {
        summary.data.push({
          count: breakdown.used,
          class: 'therm-used-licenses',
          name: transl.title_usedLicenses,
          expirations: _.map(breakdown.expiration_date_used_licenses, mapFn)
        })
      }

      if (unused > 0) {
        summary.data.push({
          count: unused,
          class: 'therm-unused-licenses',
          name: transl.title_unusedLicenses,
          expirations: _.map(breakdown.expiration_date_unused_licenses, mapFn)
        })
      }

      return summary
    }

    function _buildLicenseTimelineConfig(breakdown) {
      var data = []
      var dates = {
        now: moment(),
        inAWeek: moment().add(7, 'days'),
        inThirtyDays: moment().add(30, 'days'),
        inSixtyDays: moment().add(60, 'days')
      }
      var dateData = {
        inAWeek: {},
        inThirtyDays: {},
        inSixtyDays: {},
        inFuture: {}
      }
      var timeAccessor = function (d) {
        if (moment(d.timestamp).isAfter(dates.inSixtyDays)) {
          return moment().add(62, 'days').valueOf()
        }
        return d.timestamp
      }
      var iterateExpirations = function (item) {
        var time = moment(item.expiration)

        if (time.isBefore(dates.now)) {
          return
        }

        if (time.isBefore(dates.inAWeek)) {
          dateData.inAWeek = _addLicenseTimelineData(
            dateData.inAWeek,
            item,
            time
          )
        } else if (time.isBefore(dates.inThirtyDays)) {
          dateData.inThirtyDays = _addLicenseTimelineData(
            dateData.inThirtyDays,
            item,
            time
          )
        } else if (time.isBefore(dates.inSixtyDays)) {
          dateData.inSixtyDays = _addLicenseTimelineData(
            dateData.inSixtyDays,
            item,
            time
          )
        } else {
          dateData.inFuture = _addLicenseTimelineData(
            dateData.inFuture,
            item,
            time
          )
        }
      }

      _.each(breakdown.expiration_date_used_licenses, iterateExpirations)
      _.each(breakdown.expiration_date_unused_licenses, iterateExpirations)

      var staggeredLabels = false
      _.each(dateData, function (item, key) {
        if (angular.isDefined(item.data)) {
          item.label = _getLabel(item, key)

          if (
            data.length > 0 &&
            Math.abs(
              moment(timeAccessor(data[data.length - 1])).diff(
                moment(timeAccessor(item)),
                'days'
              )
            ) < 14
          ) {
            if (!data[data.length - 1].labelAbove) {
              item.labelAbove = true
              staggeredLabels = true
            }
          }

          data.push(item)
        }
      })

      return {
        config: {
          height: 85,
          beginning: dates.now.valueOf(),
          ending: moment().add(62, 'days').valueOf(),
          timeAccessor: timeAccessor,
          process: false,
          endpoints: false,
          pointWidth: 0,
          margin: { left: 4, right: 4, top: 30, bottom: 0 },
          linePointClassAccessor: function (d) {
            if (moment(d.timestamp).isBefore(dates.inAWeek)) {
              return 'stroke-red'
            } else if (moment(d.timestamp).isBefore(dates.inThirtyDays)) {
              return 'stroke-orange'
            } else if (moment(d.timestamp).isBefore(dates.inSixtyDays)) {
              return 'stroke-gray-light'
            }
            return 'stroke-gray-lighter'
          },
          circleClassAccessor: function (d) {
            if (moment(d.timestamp).isBefore(dates.inAWeek)) {
              return 'license-timeline-week'
            } else if (moment(d.timestamp).isBefore(dates.inThirtyDays)) {
              return 'license-timeline-month'
            } else if (moment(d.timestamp).isBefore(dates.inSixtyDays)) {
              return 'license-timeline-two-months'
            }
            return 'license-timeline-future'
          },
          label1Accessor: function (d) {
            return d.label.firstLine
          },
          label2Accessor: function (d) {
            return d.label.secondLine
          }
        },
        data: data,
        staggeredLabels: staggeredLabels
      }
    }

    function _addLicenseTimelineData(data, item, time) {
      var dataItem = {
        timestamp: time.valueOf(),
        count: item.licenses
      }

      if (angular.isUndefined(data.data)) {
        data.data = [dataItem]
        data.count = dataItem.count
        data.timestamp = dataItem.timestamp
      } else {
        data.data.push(dataItem)
        data.count += dataItem.count
        data.timestamp = _.reduce(
          data.data,
          function (currentMin, item) {
            return Math.min(currentMin, item.timestamp)
          },
          dataItem.timestamp
        )
      }

      return data
    }

    function _getLabel(data, key) {
      var transl = {
        plural_licensesReports_licensesExpire: $translate.instant(
          'plural_licensesReports_licensesExpire'
        ),
        detail_licensesReports_licensesExpireInAWeek: $translate.instant(
          'detail_licensesReports_licensesExpireInAWeek'
        ),
        detail_licensesReports_licensesExpireIn30Days: $translate.instant(
          'detail_licensesReports_licensesExpireIn30Days'
        ),
        detail_licensesReports_licensesExpireIn60Days: $translate.instant(
          'detail_licensesReports_licensesExpireIn60Days'
        ),
        detail_licensesReports_licensesExpireInTheFuture: $translate.instant(
          'detail_licensesReports_licensesExpireInTheFuture'
        )
      }

      var multiple = data.data.length > 1

      var timeString
      switch (key) {
        case 'inAWeek':
          timeString = transl.detail_licensesReports_licensesExpireInAWeek
          break

        case 'inThirtyDays':
          timeString = transl.detail_licensesReports_licensesExpireIn30Days
          break

        case 'inSixtyDays':
          timeString = transl.detail_licensesReports_licensesExpireIn60Days
          break

        default:
          timeString = transl.detail_licensesReports_licensesExpireInTheFuture
          break
      }

      return {
        multiple: multiple,
        firstLine:
          data.count +
          ' ' +
          $translate.instant(
            'plural_licensesReports_licensesExpire',
            { count: 'data.count' },
            'messageformat'
          ),
        secondLine: multiple
          ? timeString
          : moment(data.timestamp).format('MMMM D, YYYY'),
        timestamp: data.timestamp
      }
    }

    function _getAppDonutConfig(data) {
      var configs = []

      if (data.scores) {
        configs.push({
          data: _transformNVPs(data.scores, {
            value: 'value',
            name: 'name',
            group: function () {
              return ORG_STATS_CONSTANTS.APP_BREAKDOWNS.SCORES
            },
            selected: 'selected'
          }),
          valueAccessor: 'value',
          idAccessor: 'name',
          sort: 'name',
          classAccessor: _appScoreClassAccessor,
          maxWidth: CS_DONUT_CONSTANTS.DEFAULT_ARC_WIDTH * 1.2,
          selectedWidth: CS_DONUT_CONSTANTS.DEFAULT_ARC_WIDTH * 1.2,
          width: CS_DONUT_CONSTANTS.DEFAULT_ARC_WIDTH,
          tooltipTemplate: "'views/tooltips/cs-donut-tooltip.html'"
        })
      }

      if (data.technologies) {
        configs.push({
          data: _transformNVPs(data.technologies, {
            value: 'value',
            name: 'name',
            group: function () {
              return ORG_STATS_CONSTANTS.APP_BREAKDOWNS.TECHNOLOGIES
            },
            selected: 'selected'
          }),
          valueAccessor: 'value',
          idAccessor: 'name',
          sort: 'name',
          colorAccessor: d3ColorsService.getColorAccessorFromList(
            D3_CONSTANTS.CONTAINER_COLOR_PALETTE
          ),
          maxWidth: CS_DONUT_CONSTANTS.DEFAULT_ARC_WIDTH * 1.2,
          selectedWidth: CS_DONUT_CONSTANTS.DEFAULT_ARC_WIDTH * 1.2,
          width: CS_DONUT_CONSTANTS.DEFAULT_ARC_WIDTH,
          tooltipTemplate: "'views/tooltips/cs-donut-tooltip.html'"
        })
      }

      if (data.languages) {
        configs.push({
          data: _transformNVPs(data.languages, {
            value: 'value',
            name: 'name',
            group: function () {
              return ORG_STATS_CONSTANTS.APP_BREAKDOWNS.LANGUAGES
            },
            selected: 'selected'
          }),
          valueAccessor: 'value',
          idAccessor: 'name',
          sort: 'name',
          classAccessor: _appLanguageClassAccessor,
          maxWidth: CS_DONUT_CONSTANTS.DEFAULT_SECONDARY_ARC_WIDTH * 1.4,
          selectedWidth: CS_DONUT_CONSTANTS.DEFAULT_SECONDARY_ARC_WIDTH * 1.4,
          width: CS_DONUT_CONSTANTS.DEFAULT_SECONDARY_ARC_WIDTH,
          tooltipTemplate: "'views/tooltips/cs-donut-tooltip.html'"
        })
      }

      return configs
    }

    function _getAppThermometerConfig(data) {
      var transl = {
        title_appStatusLicensed: $translate.instant('title_appStatusLicensed'),
        title_applicationStatusUnlicensed: $translate.instant(
          'title_applicationStatusUnlicensed'
        ),
        title_archived_application: $translate.instant(
          'title_archived_application'
        )
      }

      var licensed = data.enterprise
      var unlicensed = data.trial
      var archived = data.archived
      var archivedLicensed = data.archived_with_license

      var thermometerData = []

      if (angular.isNumber(licensed) && licensed > 0) {
        thermometerData.push({
          count: licensed,
          class: 'therm-gray',
          name: transl.title_appStatusLicensed
        })
      }

      if (angular.isNumber(unlicensed) && unlicensed > 0) {
        thermometerData.push({
          count: unlicensed,
          class: 'therm-gray-middle',
          name: transl.title_applicationStatusUnlicensed
        })
      }

      if (angular.isNumber(archived) && archived > 0) {
        thermometerData.push({
          count: archived,
          class: 'therm-gray-lighter-middle',
          name: transl.title_archived_application,
          licensed: archivedLicensed,
          hoverable: archivedLicensed > 0
        })
      }

      return {
        count: licensed + unlicensed + archived,
        data: thermometerData
      }
    }

    function _transformAppBreakdown(data, key, whereCondition) {
      var transformedData = []
      var values = _.flatten(_.map(data, key))
      var uniqueValues = _.uniqBy(values)

      _.each(uniqueValues, function (value) {
        var whereFn = _getWhereFn(key, value, whereCondition)
        var count = _.reduce(
          data,
          function (acc, item) {
            return acc + (whereFn(item) ? 1 : 0)
          },
          0
        )

        transformedData.push({
          name: value,
          value: count
        })
      })

      return transformedData
    }

    function _getWhereFn(key, value, whereCondition) {
      var whereFn = function (item) {
        var itemValue = item[key]
        if (angular.isArray(itemValue)) {
          return _.includes(itemValue, value)
        }
        return itemValue === value
      }

      if (angular.isFunction(whereCondition)) {
        return function (item) {
          return whereFn(item) && whereCondition(item)
        }
      }
      return function (item) {
        return whereFn(item)
      }
    }

    function _getAppWhereCondition(selection) {
      switch (selection.group) {
        case ORG_STATS_CONSTANTS.APP_BREAKDOWNS.LANGUAGES:
          return function (item) {
            return item.language === selection.name
          }

        case ORG_STATS_CONSTANTS.APP_BREAKDOWNS.SCORES:
          return function (item) {
            return item.grade === selection.name
          }

        case ORG_STATS_CONSTANTS.APP_BREAKDOWNS.TECHNOLOGIES:
          return function (item) {
            return _.includes(item.technologies, selection.name)
          }

        default:
          break
      }
    }

    function _getServerDonutConfig(data, donutOptions) {
      var configs = []

      if (
        data.environments ||
        (angular.isDefined(donutOptions) &&
          _.includes(
            donutOptions,
            ORG_STATS_CONSTANTS.SERVER_BREAKDOWNS.ENVIRONMENTS
          ))
      ) {
        var envData = _transformNVPs(data.environments)
        configs.push({
          data: envData,
          valueAccessor: 'value',
          idAccessor: 'name',
          sort: 'name',
          classAccessor: _serverEnvClassAccessor(function (d) {
            return d.data.name
          }),
          tooltipTemplate: "'views/tooltips/cs-donut-tooltip.html'"
        })
      }

      if (
        data.containers ||
        (angular.isDefined(donutOptions) &&
          _.includes(
            donutOptions,
            ORG_STATS_CONSTANTS.SERVER_BREAKDOWNS.CONTAINERS
          ))
      ) {
        configs.push({
          data: _transformNVPs(data.containers, {
            value: 'count',
            name: 'container_name',
            containerVersions: 'container_versions'
          }),
          valueAccessor: 'value',
          idAccessor: 'name',
          sort: 'name',
          colorAccessor: d3ColorsService.getColorAccessorFromList(
            D3_CONSTANTS.CONTAINER_COLOR_PALETTE
          ),
          tooltipTemplate: "'views/tooltips/cs-donut-container-tooltip.html'"
        })
      }

      return configs
    }

    function _transformNVPs(items, keyValueKeys, sort) {
      var data = []
      keyValueKeys = keyValueKeys || { value: 'value', name: 'name' }

      if (angular.isDefined(sort)) {
        items = _.sortBy(items, sort)
      }

      _.each(items, function (item) {
        var datum = {}
        _.each(keyValueKeys, function (valueKey, key) {
          if (angular.isString(valueKey)) {
            datum[key] = item[valueKey]
          } else if (angular.isFunction(valueKey)) {
            datum[key] = valueKey(item)
          }
        })
        data.push(datum)
      })

      return data
    }

    function _appScoreClassAccessor(d) {
      switch (d.data.name) {
        case APPLICATION_GRADES.A:
          return 'fill-green'

        case APPLICATION_GRADES.B:
          return 'fill-teal'

        case APPLICATION_GRADES.C:
          return 'fill-yellow'

        case APPLICATION_GRADES.D:
          return 'fill-orange'

        case APPLICATION_GRADES.F:
          return 'fill-red'

        default:
          return 'fill-gray-middle'
      }
    }

    function _appLanguageClassAccessor(d, i) {
      return [
        'fill-gray-dark',
        'fill-gray-middle',
        'fill-gray-light',
        'fill-gray-lighter',
        'fill-gray-darker'
      ][i]
    }

    function _serverEnvClassAccessor(propertyAccessor) {
      return function (data) {
        switch (propertyAccessor(data)) {
          case SERVER_ENVIRONMENT.DEVELOPMENT.name:
            return 'fill-gray'

          case SERVER_ENVIRONMENT.QA.name:
            return 'fill-gray-middle'

          case SERVER_ENVIRONMENT.PRODUCTION.name:
            return 'fill-gray-lighter'

          default:
            return 'fill-gray-middle'
        }
      }
    }
  }
})()
